import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { characterLabel } from "../../domain/shopImages";
import {
  Badge,
  DragIndicatorIcon,
  IconButton,
  Menu,
  MoreVertIcon,
} from "../../themeV2";
import css from "./ShopImagesEditor.module.css";

export const tileName = (image) => characterLabel(image) ?? "Unnamed image";

export default function CharacterTile({ image, isFirst, isLast, onAction }) {
  const name = tileName(image);
  const act = (action) => () => onAction(action, image);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.imageId });

  return (
    <li
      ref={setNodeRef}
      aria-label={name}
      className={clsx(
        css.tile,
        isFirst && css.primaryTile,
        isDragging && css.dragging,
      )}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <img
        className={css.tileImage}
        src={image.url}
        alt=""
        width={150}
        height={200}
        loading="lazy"
      />
      {isFirst && (
        <Badge variant="status" status="featured" className={css.primaryBadge}>
          Primary
        </Badge>
      )}
      <div className={css.tileBar}>
        <button
          type="button"
          className={css.dragHandle}
          aria-label={`Reorder ${name}`}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon aria-hidden="true" />
        </button>
        <span className={css.tileName}>{name}</span>
        <Menu.Root>
          <Menu.Trigger
            render={
              <IconButton
                variant="text"
                size="sm"
                label={`More actions for ${name}`}
              />
            }
          >
            <MoreVertIcon />
          </Menu.Trigger>
          <Menu.Popup side="bottom" align="end">
            {!isFirst && (
              <Menu.Item onClick={act("makePrimary")}>Make primary</Menu.Item>
            )}
            <Menu.Item onClick={act("editDetails")}>Edit details</Menu.Item>
            {image.sourceUrl && (
              <Menu.Item onClick={act("recrop")}>Adjust crop</Menu.Item>
            )}
            {!isFirst && (
              <Menu.Item onClick={act("moveEarlier")}>Move earlier</Menu.Item>
            )}
            {!isLast && (
              <Menu.Item onClick={act("moveLater")}>Move later</Menu.Item>
            )}
            <Menu.Separator />
            <Menu.Item onClick={act("remove")}>Remove</Menu.Item>
          </Menu.Popup>
        </Menu.Root>
      </div>
    </li>
  );
}

CharacterTile.propTypes = {
  image: PropTypes.shape({
    imageId: PropTypes.number.isRequired,
    url: PropTypes.string.isRequired,
    sourceUrl: PropTypes.string,
    label: PropTypes.string,
    characterName: PropTypes.string,
  }).isRequired,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  onAction: PropTypes.func.isRequired,
};
